import React from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";

/* ─────────────────────────────────────────────────────────────
   CONTENU DES PAGES
   Société éditrice : TRUSTOVA SAS
   SIREN 130 856 016 — SIRET 130 856 016 00014
   TVA FR39 130 856 016
   110 rue de Fontenay, CS 20010, 94303 Vincennes Cedex
   Créée le 28/09/2026
───────────────────────────────────────────────────────────── */
const PAGES_CONTENT = {

  /* ── À PROPOS ─────────────────────────────────────────── */
  "a-propos": {
    icon: "✦",
    title: "À propos d'Univers Libertin",
    sections: [
      {
        h: "Notre vision",
        p: "Univers Libertin est la communauté libertine française de référence. Créée en 2026 par TRUSTOVA SAS, notre mission est d'offrir un espace numérique sécurisé, bienveillant et totalement confidentiel où chaque adulte consentant peut s'exprimer, partager et rencontrer librement — sans jugement, sans compromis sur la vie privée.",
      },
      {
        h: "Une plateforme 100 % française",
        p: "Développée et hébergée en France, Univers Libertin est soumise au droit français et au RGPD. Nous ne revendons aucune donnée, n'affichons aucune publicité tierce et n'utilisons aucun tracker externe. Vos données restent chez nous, pour vous servir — point.",
      },
      {
        h: "Modération par IA",
        p: "Chaque publication est analysée en temps réel par notre système de modération intelligent. Le contenu adulte entre membres consentants est pleinement autorisé. Seuls le non-consentement, le harcèlement, la discrimination et tout contenu impliquant des mineurs sont bloqués immédiatement.",
      },
      {
        h: "L'équipe",
        p: "Univers Libertin est opéré par TRUSTOVA SAS, société par actions simplifiée immatriculée sous le SIREN 130 856 016, dont le siège est situé au 110 rue de Fontenay, CS 20010, 94303 Vincennes Cedex. Une équipe passionnée de tech, de sécurité et de liberté individuelle.",
      },
    ],
  },

  /* ── COMMENT ÇA MARCHE ────────────────────────────────── */
  "comment-ca-marche": {
    icon: "🔍",
    title: "Comment ça marche",
    sections: [
      {
        h: "1. Inscription gratuite",
        p: "Créez votre compte en moins d'une minute avec un simple e-mail et un pseudo de votre choix. Confirmez votre majorité (18 ans requis) et acceptez la charte de consentement. Aucune carte bancaire exigée pour commencer.",
      },
      {
        h: "2. Créez votre profil",
        p: "Ajoutez une photo (floutable pour plus de discrétion), rédigez une bio, renseignez votre ville, votre orientation, votre situation et vos centres d'intérêt. Définissez vos limites et vos safe words directement sur votre profil.",
      },
      {
        h: "3. Explorez la communauté",
        p: "Parcourez l'annuaire filtré par ville, genre, orientation et type de recherche. Découvrez des profils qui partagent vos envies et engagez la conversation.",
      },
      {
        h: "4. Rencontres & événements",
        p: "Proposez ou rejoignez des soirées privées, des événements en clubs, des afterworks, des voyages ou des rencontres en ligne. Définissez vos critères de participation et gérez vos invités.",
      },
      {
        h: "5. Annuaire des lieux libertins",
        p: "Accédez à l'annuaire des clubs libertins, saunas, bars et espaces recommandés partout en France. Consultez les avis détaillés de la communauté sur l'ambiance, l'hygiène et le respect.",
      },
      {
        h: "6. Passez Premium",
        p: "Débloquez la messagerie illimitée, les photos supplémentaires, la liste de vos visiteurs de profil et le mode fantôme pour naviguer discrètement. Abonnement sans engagement, annulable à tout moment.",
      },
    ],
  },

  /* ── SÉCURITÉ & CONFIDENTIALITÉ ───────────────────────── */
  "securite-confidentialite": {
    icon: "🛡",
    title: "Sécurité & Confidentialité",
    sections: [
      {
        h: "Votre vie privée est notre priorité",
        p: "Univers Libertin est conçu dès sa fondation avec la confidentialité au cœur de son architecture. Pseudonymes encouragés, données non revendues, infrastructure hébergée en France.",
      },
      {
        h: "Modération IA en temps réel",
        p: "Un double système de modération analyse chaque contenu publié. Le contenu adulte entre membres consentants est pleinement autorisé. Seuls le non-consentement, le harcèlement, la discrimination et les contenus impliquant des mineurs sont immédiatement bloqués et signalés.",
      },
      {
        h: "Zéro tracking tiers",
        p: "Pas de publicité, pas de pixel Facebook ou Google, pas de cookie analytique tiers. Ce que vous faites sur Univers Libertin ne sort pas de la plateforme.",
      },
      {
        h: "Photos discrètes",
        p: "Activez l'option de flou sur vos photos : elles ne sont visibles en clair que par les membres Premium que vous autorisez. Vous gardez le contrôle total de votre image.",
      },
      {
        h: "Paiements sécurisés par Stripe",
        p: "Tous les paiements sont traités par Stripe (certifié PCI DSS niveau 1). Univers Libertin n'accède jamais à vos données bancaires. Le libellé sur votre relevé est discret.",
      },
      {
        h: "Signalement & suppression",
        p: "Signalez tout comportement suspect depuis chaque profil ou message. Supprimez votre compte à tout moment : toutes vos données sont effacées sous 30 jours.",
      },
    ],
  },

  /* ── ANNUAIRE CLUBS ────────────────────────────────────── */
  "annuaire-clubs-libertins-france": {
    icon: "📍",
    title: "Annuaire des clubs libertins",
    sections: [
      {
        h: "Les meilleurs lieux de France",
        p: "Univers Libertin référence les clubs libertins, saunas, bars échangistes et espaces privés recommandés par la communauté dans plus de 100 villes françaises. Chaque établissement est vérifié et évalué.",
      },
      {
        h: "Avis détaillés par la communauté",
        p: "Chaque lieu dispose d'avis multi-critères rédigés par des membres : note générale, ambiance, respect, hygiène, rapport qualité/prix. Des retours d'expérience authentiques, pas des étoiles génériques.",
      },
      {
        h: "Filtres avancés",
        p: "Recherchez par ville, département ou région, par catégorie (club libertin, sauna, bar, espace privé), par équipements disponibles et par gamme de prix.",
      },
      {
        h: "Vous gérez un établissement ?",
        p: "L'offre Pro vous permet de revendiquer votre fiche, ajouter des photos, publier vos événements et billets, et obtenir le badge établissement vérifié. Contactez-nous à partenariats@universlibertin.fr.",
      },
    ],
  },

  /* ── RENCONTRES ────────────────────────────────────────── */
  "rencontres-libertines": {
    icon: "🔥",
    title: "Rencontres libertines",
    sections: [
      {
        h: "Rencontres entre adultes consentants",
        p: "Univers Libertin facilite les rencontres entre adultes consentants dans un cadre bienveillant, sécurisé et sans jugement. Chaque profil affiche clairement ses attentes et ses limites.",
      },
      {
        h: "Événements variés",
        p: "Soirées privées, soirées en clubs, afterworks, voyages libertin, rencontres en ligne... Créez votre événement ou rejoignez celui d'un autre membre. Définissez les conditions de participation.",
      },
      {
        h: "Pour tous les profils",
        p: "Couples, solos, curieux·ses, expérimenté·es, tous les profils sont les bienvenus. Filtrez par type de recherche, orientation, situation, localisation et centres d'intérêt.",
      },
      {
        h: "Messagerie modérée et confidentielle",
        p: "Échangez en toute confidentialité. La messagerie est protégée par la modération IA. Bloquez ou signalez un contact en un clic. Vos messages ne sont jamais lus par notre équipe sauf signalement.",
      },
    ],
  },

  /* ── TARIFS ────────────────────────────────────────────── */
  "tarifs": {
    icon: "💜",
    title: "Nos tarifs",
    sections: [
      {
        h: "Gratuit — 0 €/mois",
        p: "Accès au fil communautaire, création de profil avec 1 photo, 3 conversations par jour, consultation de l'annuaire des membres et des lieux. Aucune carte bancaire requise.",
      },
      {
        h: "Premium — 9,99 €/mois",
        p: "Messagerie illimitée, jusqu'à 5 photos sur votre profil, liste complète de vos visiteurs de profil, filtres de recherche avancés et badge Premium visible sur votre profil. Sans engagement, annulable à tout moment.",
      },
      {
        h: "VIP — 19,99 €/mois",
        p: "Tout le Premium, plus : jusqu'à 10 photos, accusés de lecture, mode fantôme (navigation invisible), profil mis en avant dans les résultats de recherche, support client prioritaire.",
      },
      {
        h: "Pro — 49 €/mois",
        p: "Pour les établissements libertins : tableau de bord de gestion de fiche, badge établissement vérifié, galerie photos illimitée, publication d'événements et billetterie intégrée.",
      },
      {
        h: "Spotlight — 2,99 € (achat unique)",
        p: "Mise en avant de votre profil ou d'un post pendant 24 heures en tête des résultats. Idéal pour booster votre visibilité lors d'un événement ou d'une recherche de rencontre.",
      },
      {
        h: "Paiement sécurisé",
        p: "Tous les paiements sont gérés par Stripe. Le renouvellement des abonnements est automatique et vous pouvez annuler depuis votre espace personnel à tout moment. Le libellé bancaire est discret.",
      },
    ],
  },

  /* ── CONTACT ───────────────────────────────────────────── */
  "contact": {
    icon: "📧",
    title: "Nous contacter",
    sections: [
      {
        h: "Contact général",
        p: "Pour toute question relative à votre compte, à la plateforme ou à nos services : contact@universlibertin.fr — Réponse sous 48 heures ouvrées.",
      },
      {
        h: "Données personnelles & RGPD",
        p: "Pour exercer vos droits (accès, rectification, suppression, portabilité, opposition) ou pour toute question relative à la protection de vos données : dpo@universlibertin.fr",
      },
      {
        h: "Signalement de contenu",
        p: "Pour signaler un contenu inapproprié, un comportement abusif ou un profil suspect : moderation@universlibertin.fr — Traitement prioritaire sous 24 heures.",
      },
      {
        h: "Partenariats, presse & établissements",
        p: "Pour les demandes de partenariat, les gérants d'établissements souhaitant référencer leur lieu, ou les demandes presse : partenariats@universlibertin.fr",
      },
      {
        h: "Adresse postale",
        p: "TRUSTOVA SAS — 110 rue de Fontenay, CS 20010, 94303 Vincennes Cedex",
      },
    ],
  },

  /* ── MENTIONS LÉGALES ──────────────────────────────────── */
  "mentions-legales": {
    icon: "⚖",
    title: "Mentions légales",
    sections: [
      {
        h: "Éditeur du site",
        p: "TRUSTOVA SAS — Société par actions simplifiée au capital social de [CAPITAL] €. Siège social : 110 rue de Fontenay, CS 20010, 94303 Vincennes Cedex. SIREN : 130 856 016 — SIRET siège : 130 856 016 00014 — N° TVA intracommunautaire : FR39 130 856 016. Code APE : 62.01Z (Programmation informatique). Directeur de la publication : [NOM DU DIRIGEANT].",
      },
      {
        h: "Hébergement",
        p: "Le site universlibertin.fr est hébergé en France par [NOM HÉBERGEUR], [adresse hébergeur]. Les données des utilisateurs sont stockées sur des serveurs situés dans l'Union européenne.",
      },
      {
        h: "Propriété intellectuelle",
        p: "L'ensemble des contenus présents sur universlibertin.fr (textes, graphismes, logos, icônes, images, sons, vidéos, logiciels) sont protégés par le droit d'auteur et sont la propriété exclusive de TRUSTOVA SAS ou de leurs auteurs respectifs. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, est strictement interdite sans autorisation préalable écrite.",
      },
      {
        h: "Données personnelles",
        p: "Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, d'opposition, de suppression et de portabilité de vos données. Pour exercer ces droits : dpo@universlibertin.fr",
      },
      {
        h: "Loi applicable & juridiction",
        p: "Les présentes mentions légales sont régies par le droit français. En cas de litige, les tribunaux compétents du ressort du siège social de TRUSTOVA SAS seront seuls compétents.",
      },
      {
        h: "Contact",
        p: "Pour toute question : contact@universlibertin.fr — TRUSTOVA SAS, 110 rue de Fontenay, CS 20010, 94303 Vincennes Cedex.",
      },
    ],
  },

  /* ── POLITIQUE DE CONFIDENTIALITÉ ─────────────────────── */
  "politique-de-confidentialite": {
    icon: "🔒",
    title: "Politique de confidentialité",
    sections: [
      {
        h: "Responsable du traitement",
        p: "TRUSTOVA SAS, SIREN 130 856 016, 110 rue de Fontenay, CS 20010, 94303 Vincennes Cedex — dpo@universlibertin.fr",
      },
      {
        h: "Données collectées",
        p: "Données d'inscription : adresse e-mail, pseudo. Données de profil (facultatives) : ville, département, orientation, situation, bio, photos. Contenus publiés : posts, commentaires, messages. Données de paiement : traitées exclusivement par Stripe, jamais stockées sur nos serveurs. Données techniques : adresse IP, logs de connexion, type de navigateur.",
      },
      {
        h: "Finalités du traitement",
        p: "Fourniture et gestion du service. Modération automatisée par IA des contenus. Gestion des abonnements et paiements. Amélioration de la plateforme sur la base de statistiques anonymisées. Respect des obligations légales.",
      },
      {
        h: "Partage des données",
        p: "Vos données ne sont jamais vendues ni louées à des tiers. Elles sont uniquement partagées avec nos sous-traitants techniques dans le cadre strict de leurs missions : prestataire d'hébergement (stockage), Stripe (paiements sécurisés), système de modération IA (analyse anonymisée des contenus).",
      },
      {
        h: "Vos droits RGPD",
        p: "Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression (droit à l'oubli), de portabilité, d'opposition et de limitation du traitement. Pour exercer ces droits : dpo@universlibertin.fr — Réponse sous 30 jours. Vous pouvez également déposer une réclamation auprès de la CNIL (www.cnil.fr).",
      },
      {
        h: "Cookies",
        p: "Nous n'utilisons que des cookies strictement fonctionnels nécessaires au fonctionnement de la plateforme (session, préférences de langue). Aucun cookie publicitaire ou de tracking tiers n'est déposé sur votre appareil.",
      },
      {
        h: "Durées de conservation",
        p: "Compte actif : données conservées tant que le compte est actif. Compte supprimé : données effacées sous 30 jours. Messages : 12 mois après suppression de la conversation. Logs de modération : 6 mois. Factures et données de paiement : 10 ans (obligation légale).",
      },
      {
        h: "Sécurité",
        p: "Les données sont stockées sur des serveurs hébergés dans l'Union européenne. Les mots de passe sont hachés avec bcrypt. Les communications sont chiffrées en TLS. L'accès aux données de production est strictement contrôlé.",
      },
    ],
  },

  /* ── CGU ───────────────────────────────────────────────── */
  "conditions-generales-utilisation": {
    icon: "📋",
    title: "Conditions générales d'utilisation",
    sections: [
      {
        h: "Objet",
        p: "Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme Univers Libertin, éditée par TRUSTOVA SAS (SIREN 130 856 016), accessible à l'adresse universlibertin.fr.",
      },
      {
        h: "Accès & majorité",
        p: "La plateforme est réservée exclusivement aux personnes majeures (18 ans révolus). En créant un compte, vous certifiez avoir 18 ans ou plus. Toute inscription par un mineur entraîne la suppression immédiate et définitive du compte, et peut faire l'objet d'un signalement aux autorités compétentes.",
      },
      {
        h: "Création de compte",
        p: "Un compte est créé avec une adresse e-mail valide et un mot de passe. L'utilisation d'un pseudonyme est encouragée. Vous êtes responsable de la confidentialité de vos identifiants et de toute activité effectuée depuis votre compte.",
      },
      {
        h: "Contenus autorisés",
        p: "Les discussions et contenus explicites entre adultes consentants sont autorisés. Sont strictement interdits : tout contenu impliquant ou suggérant le non-consentement, le harcèlement, la discrimination, des mineurs, de la prostitution ou du proxénétisme, du spam ou de la publicité non autorisée.",
      },
      {
        h: "Modération",
        p: "TRUSTOVA SAS se réserve le droit de supprimer tout contenu et de suspendre ou bannir tout compte en violation des présentes CGU, sans préavis ni indemnité pour les violations graves.",
      },
      {
        h: "Abonnements & paiements",
        p: "Les abonnements payants sont gérés par Stripe. Le renouvellement est automatique à l'échéance de la période souscrite. Vous pouvez annuler à tout moment depuis votre espace personnel ; l'accès Premium reste actif jusqu'à la fin de la période déjà payée. Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux contenus numériques dont l'exécution a commencé.",
      },
      {
        h: "Propriété intellectuelle",
        p: "Les contenus publiés par les membres restent leur propriété. En les publiant sur la plateforme, vous accordez à TRUSTOVA SAS une licence non exclusive d'hébergement et d'affichage dans le cadre du service.",
      },
      {
        h: "Responsabilité",
        p: "TRUSTOVA SAS n'est pas responsable des contenus publiés par les membres. Elle s'engage à mettre en œuvre tous les moyens raisonnables pour assurer la sécurité de la plateforme mais ne peut garantir son absence d'interruption.",
      },
      {
        h: "Résiliation",
        p: "Vous pouvez supprimer votre compte à tout moment depuis votre espace personnel. TRUSTOVA SAS peut résilier un compte sans préavis en cas de violation grave des présentes CGU.",
      },
      {
        h: "Modifications",
        p: "TRUSTOVA SAS se réserve le droit de modifier les présentes CGU. Les utilisateurs seront informés par e-mail ou notification sur la plateforme. L'utilisation continuée après modification vaut acceptation des nouvelles CGU.",
      },
      {
        h: "Droit applicable",
        p: "Les présentes CGU sont régies par le droit français. Tout litige relèvera de la compétence exclusive des tribunaux du ressort du siège de TRUSTOVA SAS.",
      },
    ],
  },

  /* ── CHARTE ────────────────────────────────────────────── */
  "charte-communautaire": {
    icon: "✦",
    title: "Charte communautaire",
    sections: [
      {
        h: "1. Consentement absolu",
        p: "Le consentement est la valeur fondamentale de notre communauté. Un « non », un silence ou une hésitation n'est jamais un « oui ». Toute pression, insistance ou manipulation est strictement prohibée et entraîne un bannissement immédiat.",
      },
      {
        h: "2. Confidentialité totale",
        p: "Ce qui se passe sur Univers Libertin reste sur Univers Libertin. Ne jamais partager l'identité réelle, les photos ou les activités d'un autre membre en dehors de la plateforme sans son autorisation explicite. Tout outing volontaire entraîne un bannissement immédiat.",
      },
      {
        h: "3. Respect des limites",
        p: "Les limites et safe words affichés sur les profils doivent être lus et respectés avant tout contact. Aucune moquerie, aucun commentaire dévalorisant sur les pratiques ou les limites d'autrui ne sera toléré.",
      },
      {
        h: "4. Inclusivité & non-discrimination",
        p: "Toutes les orientations sexuelles, identités de genre, morphologies et pratiques consenties entre adultes sont les bienvenues. Aucune discrimination fondée sur l'âge (dans les limites légales), le sexe, l'orientation, l'identité de genre, la race, la religion ou le handicap n'est tolérée.",
      },
      {
        h: "5. Protection des mineurs",
        p: "Tolérance zéro absolue. Tout contenu impliquant, suggérant ou s'adressant à des mineurs entraîne un bannissement définitif et un signalement immédiat aux autorités compétentes.",
      },
      {
        h: "6. Authenticité & honnêteté",
        p: "Les faux profils, le catfishing, l'usurpation d'identité, le spam et la publicité non autorisée sont interdits. Soyez honnête sur qui vous êtes et ce que vous cherchez.",
      },
      {
        h: "7. Sanctions",
        p: "Tout manquement à la présente charte est susceptible d'entraîner un avertissement, une suspension temporaire ou un bannissement définitif, selon la gravité. Les violations graves (non-consentement, mineurs, outing) sont traitées avec bannissement immédiat sans avertissement préalable.",
      },
    ],
  },
};

/* ─────────────────────────────────────────────────────────────
   COMPOSANT
───────────────────────────────────────────────────────────── */
export default function StaticPage() {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const location = useLocation();

  const slug = paramSlug || location.pathname.replace(/^\//, "");
  const page = PAGES_CONTENT[slug];

  if (!page) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
        <div className="mb-3 text-4xl font-bold text-white/20">404</div>
        <h1 className="text-2xl font-bold text-white">Page introuvable</h1>
        <Link to="/" className="mt-4 rounded-lg bg-violet-600 px-6 py-2 text-sm font-semibold text-white hover:bg-violet-500">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  const isLegal = ["mentions-legales", "politique-de-confidentialite", "conditions-generales-utilisation", "charte-communautaire"].includes(slug);

  return (
    <div className="mx-auto max-w-3xl py-4">

      {/* Breadcrumb */}
      <nav className="mb-6 flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.3)" }}>
        <Link to="/" className="transition-colors hover:text-white/60">Accueil</Link>
        <span>›</span>
        <span style={{ color: "rgba(255,255,255,0.5)" }}>{page.title}</span>
      </nav>

      {/* Titre */}
      <div className="mb-10">
        <span className="text-3xl">{page.icon}</span>
        <h1 className="mt-3 text-3xl font-bold text-white sm:text-4xl">{page.title}</h1>
        {isLegal && (
          <p className="mt-2 text-sm" style={{ color: "rgba(255,255,255,0.3)" }}>
            Dernière mise à jour : octobre 2026 — TRUSTOVA SAS
          </p>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-8">
        {page.sections.map((section, i) => (
          <div key={i}
            className={isLegal
              ? "border-l-2 pl-5"
              : "rounded-xl p-5"
            }
            style={isLegal
              ? { borderColor: "rgba(139,111,245,0.3)" }
              : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(139,111,245,0.1)" }
            }
          >
            <h2 className="mb-2 text-base font-semibold" style={{ color: "#c4b5fd" }}>{section.h}</h2>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.6)" }}>{section.p}</p>
          </div>
        ))}
      </div>

      {/* CTA si page non légale */}
      {!isLegal && (
        <div className="mt-14 rounded-2xl p-8 text-center"
          style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.15),rgba(91,33,182,0.1))", border: "1px solid rgba(139,111,245,0.2)" }}>
          <p className="mb-4 text-lg font-semibold text-white">Rejoindre Univers Libertin</p>
          <button onClick={() => navigate("/welcome")}
            className="rounded-lg px-8 py-3 text-sm font-bold text-white transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg,#7c3aed,#5b21b6)" }}>
            Inscription gratuite
          </button>
          <p className="mt-3 text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>Gratuit · Confidentiel · Modération IA · +18</p>
        </div>
      )}
    </div>
  );
}
